<template>
  <a-dropdown v-model:open="dropdownOpen" trigger="click" @openChange="onOpenChange">
    <a-button size="small" class="pie-filter-trigger">
      {{ labelText }}
      <DownOutlined class="pie-filter-icon" />
    </a-button>
    <template #overlay>
      <div class="pie-filter-dropdown" @click.stop>
        <a-checkbox :checked="checkAll" :indeterminate="indeterminate" @change="onCheckAllChange">
          全选
        </a-checkbox>
        <a-divider style="margin: 8px 0;" />
        <div class="pie-filter-options-scroll">
          <div class="pie-filter-options-content">
            <a-checkbox-group v-model:value="checkedListModel" :class="['pie-filter-options', { 'multi-column': useMultiColumn }]">
              <a-checkbox v-for="item in options" :key="item" :value="item" class="pie-filter-option-item">
                {{ item }}
              </a-checkbox>
            </a-checkbox-group>
          </div>
        </div>
      </div>
    </template>
  </a-dropdown>
</template>

<script setup>
import { computed, ref } from 'vue';
import { DownOutlined } from '@ant-design/icons-vue';

/* global defineProps, defineEmits */
/**
 * MyDropdownCheckbox
 *
 * 可复用的筛选下拉组件，支持：
 * - 全选/半选态展示
 * - 外部双向绑定选中项
 * - 自定义按钮文案（显示已选/总数等）
 *
 * 基本用法：
 * <my-dropdown-checkbox
 *   v-model:checkedList="selected"
 *   :options="options"
 * />
 *
 * 自定义按钮文案：
 * <my-dropdown-checkbox
 *   v-model:checkedList="selected"
 *   :options="options"
 *   :label-formatter="(checked, total) => `筛选 ${checked}/${total}`"
 * />
 */
const props = defineProps({
  /**
   * 选项列表（字符串数组）。
   * 示例：['伙伴：哈勃', '高校', '其他']
   */
  options: {
    type: Array,
    default: () => []
  },
  /**
   * 已选项（与 options 保持同类型，通常为字符串数组）。
   * 使用 v-model:checkedList 进行双向绑定。
   * 示例：['高校', '其他']
   */
  checkedList: {
    type: Array,
    default: () => []
  },
  /**
   * 自定义按钮文案的格式化函数。
   * 入参：checked 已选数量，total 总数量；返回显示文本。
   * 示例：(checked, total) => `筛选 ${checked}/${total}`
   */
  labelFormatter: {
    type: Function,
    default: null
  }
});

const emit = defineEmits(['update:checkedList']);

const dropdownOpen = ref(false);
const useMultiColumn = computed(() => props.options.length > 8);

const checkedListModel = computed({
  get: () => props.checkedList,
  set: (val) => {
    emit('update:checkedList', val);
    dropdownOpen.value = true;
  }
});

const labelText = computed(() => {
  if (typeof props.labelFormatter === 'function') {
    return props.labelFormatter(props.checkedList.length, props.options.length);
  }
  return '筛选';
});

const checkAll = computed(() => {
  const total = props.options.length;
  return total > 0 && props.checkedList.length === total;
});

const indeterminate = computed(() => {
  const total = props.options.length;
  return props.checkedList.length > 0 && props.checkedList.length < total;
});

const onCheckAllChange = (e) => {
  const next = e.target.checked ? [...props.options] : [];
  emit('update:checkedList', next);
  dropdownOpen.value = true;
};

const onOpenChange = (nextOpen) => {
  dropdownOpen.value = nextOpen;
};
</script>

<style scoped>
.pie-filter-dropdown {
  padding: 14px 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-height: 140px;
  max-height: 320px;
  overflow: hidden;
}

.pie-filter-options-scroll {
  max-height: 220px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  padding-right: 4px;
}

:deep(.pie-filter-options) {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

:deep(.pie-filter-options.multi-column) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}

:deep(.pie-filter-options .pie-filter-option-item) {
  display: flex;
  margin-inline-start: 0;
  min-width: 0;
}

:deep(.pie-filter-options .ant-checkbox-wrapper) {
  display: inline-flex;
  align-items: center;
  line-height: 1.2;
}

.pie-filter-trigger {
  min-width: 120px;
  height: 30px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
}

.pie-filter-icon {
  margin-left: 6px;
  font-size: 12px;
  color: #BFBFBF;
}
</style>
